/**
 * O botão da previsão (relógio com seta), pedido do usuário em 08/10/2026:
 * - na barra de tempo (desktop), à direita do eixo, só o ícone; ao passar o
 *   mouse (ou com o foco pelo teclado), a explicação do que a previsão é — uma
 *   extrapolação da última imagem, não um modelo de previsão do tempo —, para
 *   quem usa a ferramenta não se enganar;
 * - no celular, no canto inferior direito do mapa, onde ficava a legenda
 *   recolhida (o rastreio já vem com o satélite; o que importa ter à mão é a
 *   previsão).
 * Desligada, um toque liga na ÚLTIMA imagem rastreada e já TOCA a previsão (o
 * fluxo: a imagem e cada antecedência em sequência) — faz as vezes do ▶ que a
 * zona tinha; ligada, desliga.
 */
import clsx from 'clsx';
import { ClockArrowRight } from 'lucide-react';
import { useId } from 'react';
import { formatTimeBrt } from '../lib/time';
import { useGoesStore } from '../state/goes';
import { useTimeStore } from '../state/time';
import { BASE_RASTREIO } from './api';
import { showToast } from '../state/toasts';
import { tocarPrevisao } from './fluxo';
import { useTrackingStore } from './store';
import { quadroMaisNovoComPrevisao } from './tempo';

/** O instante da última imagem rastreada do loop (de onde a previsão parte), ou null. */
function useUltimaRastreada(): number | null {
  const frames = useTimeStore((s) => s.frames);
  const indice = useTrackingStore((s) => s.indice);
  if (!indice) return null;
  const i = quadroMaisNovoComPrevisao(frames, indice.quadros);
  return i >= 0 ? (frames[i] ?? null) : null;
}

/** Desligada: liga na última imagem rastreada e toca. Ligada: desliga. Sem nenhuma imagem com previsão, diz por quê. */
export function alternarPrevisao(): void {
  const st = useTrackingStore.getState();
  if (st.previsao) {
    st.setPrevisao(false);
    return;
  }
  tocarPrevisao();
  if (!useTrackingStore.getState().previsao) {
    showToast({ id: 'sem-previsao', kind: 'info', message: 'Nenhuma imagem do período tem previsão ainda: o rastreio chega 15–25 min depois da imagem.' });
  }
}

/**
 * Celular: o botão no canto inferior direito do mapa, logo acima da legenda recolhida (pedido do
 * usuário, 08/10/2026; antes ficava na coluna, abaixo dos "últimos focos", e depois acima da
 * assinatura do pyForTraCC, que foi para o canto esquerdo com o trilho de camadas).
 */
export function BotaoDaPrevisaoNoMapa(props: { naLinha?: boolean } = {}) {
  const ligada = useTrackingStore((s) => s.previsao);
  const satelite = useGoesStore((s) => s.layers.some((l) => l.visible));
  // Como o da barra: sem o satélite (de que a previsão é subcamada) não há o que ligar.
  if (!BASE_RASTREIO || !satelite) return null;
  return (
    <button
      type="button"
      onClick={alternarPrevisao}
      aria-pressed={ligada}
      aria-label={ligada ? 'Desligar a previsão das nuvens' : 'Ligar e tocar a previsão das nuvens (extrapolação da última imagem)'}
      // A ficha do mapa desvia da coluna de botões (map/popupArea `SELETOR_DA_COLUNA`).
      data-tracking-tab
      data-botao-previsao
      className={clsx(
        'pointer-events-auto inline-flex size-11 items-center justify-center rounded-xl border shadow-lg focus-visible:outline-2 focus-visible:outline-mata-400',
        // Na linha do pé do celular (ui/RelogioDoPe), no canto direito, onde ficava a legenda recolhida
        // (que saiu do mapa e foi para o painel da camada; pedido do usuário, 29/10/2026).
        !props.naLinha && 'absolute right-[calc(8px+env(safe-area-inset-right))] bottom-2 z-10',
        ligada ? 'border-[var(--color-previsao)] bg-ink-900/95' : 'border-ink-600/90 bg-ink-900/95',
      )}
    >
      <ClockArrowRight className={clsx('size-5', ligada ? 'text-[var(--color-previsao)]' : 'text-ink-200')} aria-hidden />
    </button>
  );
}

export function BotaoDaPrevisao() {
  const ligada = useTrackingStore((s) => s.previsao);
  const satelite = useGoesStore((s) => s.layers.some((l) => l.visible));
  const origem = useUltimaRastreada();
  const metodo = useTrackingStore((s) => s.indice?.metodo ?? 'persistencia');
  const dica = useId();
  if (!BASE_RASTREIO || !satelite) return null;
  return (
    <div className="group relative shrink-0">
      <button
        type="button"
        onClick={alternarPrevisao}
        aria-pressed={ligada}
        aria-label={ligada ? 'Desligar a previsão das nuvens' : 'Ligar e tocar a previsão das nuvens'}
        aria-describedby={dica}
        data-botao-previsao
        className={clsx(
          'inline-flex size-8 items-center justify-center rounded-lg border transition-colors',
          'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400',
          ligada
            ? 'border-[var(--color-previsao)] bg-[color-mix(in_srgb,var(--color-previsao)_18%,transparent)] text-white'
            : 'border-ink-600 bg-ink-800 text-ink-100 hover:bg-ink-700',
        )}
      >
        <ClockArrowRight className={clsx('size-4', ligada ? 'text-[var(--color-previsao)]' : 'text-ink-300')} aria-hidden />
      </button>
      {/* A dica: no hover e no foco pelo teclado, acima do botão (a barra fica no pé da tela). */}
      <div
        id={dica}
        role="tooltip"
        className={clsx(
          'pointer-events-none invisible absolute right-0 bottom-full z-30 mb-2 w-[300px] rounded-lg border border-ink-600 bg-ink-900 p-2.5 text-left text-[11px] leading-snug text-ink-200 opacity-0 shadow-xl transition-opacity',
          // Foco só pelo teclado: depois de um clique a dica não fica aberta sobre o mapa.
          'group-hover:visible group-hover:opacity-100 group-has-[:focus-visible]:visible group-has-[:focus-visible]:opacity-100',
        )}
      >
        {metodo === 'rede' ? (
          <>
            <p className="mb-1 text-xs font-semibold text-white">Previsão de nuvens · rede neural (experimental)</p>
            <p>
              Cada sistema de nuvens da última imagem rastreada{origem !== null ? ` (${formatTimeBrt(origem)})` : ''} segue o
              deslocamento, o crescimento ou o encolhimento que uma rede neural recorrente (GRU do pyForTraCC) prevê a partir da
              história recente dele, de 30 em 30 min até +3 h.
            </p>
            <p className="mt-1 text-ink-200">Um clique liga e toca a previsão; outro, desliga.</p>
            <p className="mt-1 text-ink-300">
              Ainda é uma extrapolação dos sistemas da última imagem, não um modelo de previsão do tempo: não prevê o nascimento de
              nuvens novas, e a incerteza cresce rápido depois da primeira hora.
            </p>
          </>
        ) : (
          <>
            <p className="mb-1 text-xs font-semibold text-white">Previsão de nuvens · extrapolação</p>
            <p>
              Cada sistema de nuvens da última imagem rastreada{origem !== null ? ` (${formatTimeBrt(origem)})` : ''} é deslocado com
              a velocidade e a direção médias das últimas imagens, de 30 em 30 min até +3 h, mantendo a forma e o tamanho
              (persistência do pyForTraCC).
            </p>
            <p className="mt-1 text-ink-200">Um clique liga e toca a previsão; outro, desliga.</p>
            <p className="mt-1 text-ink-300">
              É só uma extrapolação da última imagem, não um modelo de previsão do tempo: não prevê o nascimento, o crescimento nem a
              dissipação das nuvens.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
